import { cloneDeep, isDate } from 'lodash';
import type { MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { combineRefs } from '../../../common/functions/combine-refs';
import useTestTag from '../../../common/hooks/use-test-tag';
import { IconButton } from '../../button/button';
import { ContinuousScrollerComparisonType } from '../../continuous-scroller/continuous-scroller-types';
import {
  formatDateRange,
  getCustomDateRangeItem,
  getDateRanges,
} from '../../date-filter-overlay-panel/date-range-filter-functions';
import type { DateRange } from '../../date-filter-overlay-panel/date-range-filter.component';
import DateRangeFilter from '../../date-filter-overlay-panel/date-range-filter.component';
import type { DropDownResult } from '../../fields/drop-down/drop-down-types';
import { OdinIconSize } from '../../odin-icon/odin-icon.component';
import { hasColumnChanged } from '../data-grid-functions';
import type { DataGridColumnSettings } from '../data-grid-settings.interface';
import FilterChip from './common/filter-chip.component';
import type { DataGridFilterProps } from './data-grid-filter.interface';

export default function DataGridDateFilter({
  column,
  filterSignal,
  scrollDetection,
  forceResetColumnMap,
}: DataGridFilterProps<DateRange>) {
  const [currentValue, setCurrentValue] = useState<DateRange | undefined>(
    transformDataGridColumnSearchValueToDateRange(column.searchValue)
  );

  const [filterOverlayVisible, setFilterOverlayVisible] = useState(false);
  const [selectedRange, setSelectedRange] = useState<string | undefined>(
    column.customDataProperties?.dateRange as string | undefined
  );

  const previousColumn = useRef<DataGridColumnSettings | undefined>(undefined);
  const columnRef = useRef<HTMLDivElement | null>(null);

  const tagCreator = useTestTag(column.title || '');
  const combinedRef = combineRefs(columnRef, tagCreator('date-filter'));

  useEffect(() => {
    if (hasColumnChanged(forceResetColumnMap, column, previousColumn.current)) {
      const value = transformDataGridColumnSearchValueToDateRange(column.searchValue);
      setCurrentValue(value);

      if (column.customDataProperties?.dateRange) {
        const dateRange = getDateRanges().find((range) => range.value === column.customDataProperties!.dateRange);
        setSelectedRange(dateRange?.text);
      } else if (value?.from || value?.to) {
        setSelectedRange(formatDateRange(value?.from, value?.to));
      } else {
        setSelectedRange(undefined);
      }
    }

    previousColumn.current = column;
  }, [column, forceResetColumnMap]);

  useEffect(() => {
    if (currentValue && (currentValue.from || currentValue.to)) {
      const filterIndex = filterSignal.value.findIndex((filter) => filter.scrollerId === column.id);

      // Only add if filter doesn't already exist for this column
      if (filterIndex === -1) {
        const clonedFilters = cloneDeep(filterSignal.value);

        const continuousScrollerFilter = transformDateRangeToContinuousScrollerSearchValueFilter(
          column.id,
          currentValue,
          column.field,
          column.customDataProperties
        );

        clonedFilters.push(continuousScrollerFilter!);
        filterSignal.value = clonedFilters;
      }

      if (column.customDataProperties?.dateRange) {
        const dateRange = getDateRanges().find((range) => range.value === column.customDataProperties!.dateRange);
        setSelectedRange(dateRange?.text);
      } else {
        setSelectedRange(formatDateRange(currentValue?.from, currentValue?.to));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Mount-only effect to initialize filter from initial column values
  }, []);

  const onDateRangeChange = (dateRange: DateRange, quickDateRange?: string) => {
    if (dateRange && (dateRange.from || dateRange.to)) {
      const filterIndex = filterSignal.value.findIndex((filter) => filter.scrollerId === column.id);
      const clonedFilters = cloneDeep(filterSignal.value);

      const customDataProperties = {
        ...column.customDataProperties,
        dateRange: quickDateRange,
      };

      const continuousScrollerFilter = transformDateRangeToContinuousScrollerSearchValueFilter(
        column.id,
        dateRange,
        column.field,
        customDataProperties
      );

      if (filterIndex > -1) {
        clonedFilters.splice(filterIndex, 1, continuousScrollerFilter!);
        filterSignal.value = clonedFilters;
      } else {
        clonedFilters.push(continuousScrollerFilter!);
        filterSignal.value = clonedFilters;
      }

      setCurrentValue(dateRange);
    } else {
      if (filterSignal.value.findIndex((filter) => filter.scrollerId === column.id) > -1) {
        filterSignal.value = filterSignal.value.filter((filter) => filter.scrollerId !== column.id);
      }
      setCurrentValue(undefined);
    }
  };

  const showFilterOverlay = () => {
    setFilterOverlayVisible(true);
  };

  const handleClearFilter = () => {
    setFilterOverlayVisible(false);
    setSelectedRange(undefined);
    onDateRangeChange({});
  };

  const handleHidePanel = () => {
    setFilterOverlayVisible(false);
  };

  const handleApplyFilter = (dateRange: DateRange, quickDateRange?: DropDownResult) => {
    setFilterOverlayVisible(false);

    if (getCustomDateRangeItem().value === quickDateRange?.value) {
      setSelectedRange(formatDateRange(dateRange.from, dateRange.to));
      onDateRangeChange(dateRange);
    } else {
      setSelectedRange(quickDateRange?.text);
      onDateRangeChange(dateRange, quickDateRange?.value ? String(quickDateRange.value) : undefined);
    }
  };

  const handleFilterChipClear = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    handleClearFilter();
  };

  return (
    <div className="odin-flex odin-h-full" ref={combinedRef}>
      <div
        className="odin-text-xs odin-w-full odin-flex odin-items-center odin-cursor-pointer odin-border odin-bg-mono-1 odin-border-gray-4"
        onClick={showFilterOverlay}
      >
        <FilterChip text={selectedRange} onClear={handleFilterChipClear} />
        <IconButton classNames="odin-bg-transparent odin-pr-1" iconSize={OdinIconSize.ExtraSmall} icon="Calendar" />
      </div>
      <DateRangeFilter
        target={columnRef.current}
        value={currentValue}
        selectedQuickDateRange={selectedRange}
        visible={filterOverlayVisible}
        scrollDetection={scrollDetection}
        onApplyFilter={handleApplyFilter}
        onClearFilter={handleClearFilter}
        onHidePanel={handleHidePanel}
      />
    </div>
  );
}

// Transforms a column configuration search value into a DateRange
export const transformDataGridColumnSearchValueToDateRange = (
  searchValue?: string | boolean | DropDownResult | Array<DropDownResult> | Date[] | Date,
  comparison?: string
) => {
  if (searchValue === undefined) {
    return undefined;
  } else if (Array.isArray(searchValue) && searchValue.length >= 1) {
    return {
      from: isDate(searchValue[0]) ? searchValue[0] : undefined,
      to: isDate(searchValue[1]) ? searchValue[1] : undefined,
    };
  } else if (comparison === undefined || !isDate(searchValue)) {
    return undefined;
  } else {
    // we have only one date, so it's either a from or to date based on comparison type
    switch (comparison) {
      case '>=':
        return { from: searchValue as Date, to: undefined };
      case '<=':
        return { from: undefined, to: searchValue as Date };
      default:
        return undefined;
    }
  }
};

// Transforms the DataRange value into a continuous scroller filter
export const transformDateRangeToContinuousScrollerSearchValueFilter = (
  scrollerId: string | number,
  dateRange: DateRange,
  field: string,
  customDataProperties?: Record<string, unknown>
) => {
  const { from, to } = dateRange;

  if (from && to) {
    return {
      scrollerId,
      field,
      value: [from, to],
      comparison: ContinuousScrollerComparisonType.Between,
      customDataProperties,
    };
  } else if (from && !to) {
    return {
      scrollerId,
      field,
      value: from,
      comparison: ContinuousScrollerComparisonType.GreaterThanEqualTo,
      customDataProperties,
    };
  } else if (!from && to) {
    return {
      scrollerId,
      field,
      value: to,
      comparison: ContinuousScrollerComparisonType.LessThanEqualTo,
      customDataProperties,
    };
  }
};
